import React, { useState } from 'react';
import { 
  Row, Col, Card, Table, Tag, Button, Space, Typography, 
  Input, Tooltip, Progress, Alert, Popover, Empty
} from 'antd';
import { 
  InfoCircleOutlined,
  BulbOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { OTR_THRESHOLDS } from '../utils/OTRDataService';
import { analyzeStopWithRuleset } from '../utils/OTRKPIAIRuleset';
import OTRInsightCard from './OTRInsightCard';
import otrDataProcessor from '../utils/OTRDataProcessor';
import useAllStops from '../../../hooks/useAllStops';

const { Text } = Typography;

const OTRStopPerformanceTab = ({ 
  stopData, 
  onStopClick,
  tripData,
  tripStartTimes,
  treatSchoolOutEarlyAsOnTime = false
}) => {
  const [stopSearchText, setStopSearchText] = useState('');
  const [selectedStopDetail, setSelectedStopDetail] = useState(null);
  const [stopTripData, setStopTripData] = useState([]);
  const { allStopsByStopCode } = useAllStops();

  // Function to get stop name by TSN (exact match with stopCode)
  const getStopName = (tsn) => {
    if (!allStopsByStopCode || !tsn) return null;
    
    // Find stop with exact matching stopCode
    const stop = allStopsByStopCode[String(tsn)];
    return stop ? stop.stopName : null;
  };

  if (stopData.length === 0) {
    return (
      <Empty 
        description="No stop data available" 
        image={Empty.PRESENTED_IMAGE_SIMPLE} 
      />
    );
  }

  // Use centralized data processor for stop statistics
  const stopStats = otrDataProcessor.calculateStopStatistics(stopData, treatSchoolOutEarlyAsOnTime);
  
  // Store string version of routes for display after analysis is done
  const stopStatsArray = Object.values(stopStats).map(stop => {
    // Deep clone to avoid reference issues
    const stopCopy = { ...stop };
    // Save routes as string for display
    stopCopy.routesString = Array.from(stop.routes).join(', ');
    // Get the correct stop name from Planable Points data
    const correctStopName = getStopName(stop.tsn);
    if (correctStopName) {
      stopCopy.stopName = correctStopName;
    }
    // Trip count is already pre-calculated in the data processor
    
    return stopCopy;
  });

  // Get stops with potential positioning issues
  const stopsWithIssues = stopStatsArray
    .filter(stop => stop.potentialIssues && stop.potentialIssues.length > 0)
    .sort((a, b) => b.positioningIssueScore - a.positioningIssueScore);
  
  const handleStopClick = async (tsn) => {
    const selectedStop = stopStatsArray.find(stop => stop.tsn === tsn);
    setSelectedStopDetail(selectedStop);
    
    // Filter trip data for trips that pass through this stop
    // We need to find all trips that have records for this TSN
    const tripsWithStop = [...new Set(stopData
      .filter(record => record.TSN === tsn)
      .map(record => record.TRIP_ID)
      .filter(Boolean)
    )];
    
    // Get trip data for these trips and add stop-specific information
    const stopTrips = tripData
      .filter(trip => tripsWithStop.includes(trip.tripId))
      .map(trip => {
        // Find the stop record for this trip and stop combination
        const stopRecord = stopData.find(record => 
          (record.KEY || record.TRIP_ID) === trip.tripKey && record.TSN === tsn
        );
        
        // Determine stop type for this trip
        let stopType = 'Unknown';
        if (stopRecord?.TSN_FIRST_LAST_IND === 'F') stopType = 'First';
        else if (stopRecord?.TSN_FIRST_LAST_IND === 'M') stopType = 'Mid';
        else if (stopRecord?.TSN_FIRST_LAST_IND === 'L') stopType = 'Last';
        else if (!stopRecord?.TSN_FIRST_LAST_IND) stopType = 'Mid'; // Default for empty
        
        return {
          ...trip,
          stopType,
          stopRecord
        };
      });
    
    setStopTripData(stopTrips);
  };

  const renderStopTripView = () => {
    if (!selectedStopDetail || stopTripData.length === 0) {
      return null;
    }

    const columns = [
      {
        title: 'Trip Start',
        dataIndex: 'tripId',
        key: 'tripId',
        sorter: (a, b) => String(a.tripId || '').localeCompare(String(b.tripId || '')),
        render: (_, record) => tripStartTimes[record.tripKey]?.time || record.tripId,
      },
      {
        title: 'Route',
        dataIndex: 'route',
        key: 'route',
        sorter: (a, b) => String(a.route || '').localeCompare(String(b.route || '')),
      },
      {
        title: 'Stop Type',
        dataIndex: 'stopType',
        key: 'stopType',
        render: stopType => {
          let color = 'default';
          if (stopType === 'First') color = 'green';
          if (stopType === 'Mid') color = 'blue';
          if (stopType === 'Last') color = 'orange';
          
          return <Tag color={color}>{stopType}</Tag>;
        },
        filters: [
          { text: 'First', value: 'First' },
          { text: 'Mid', value: 'Mid' },
          { text: 'Last', value: 'Last' }
        ],
        onFilter: (value, record) => record.stopType === value,
      },
      {
        title: 'Overall Trip OTR %',
        dataIndex: ['overallStats', 'onTimePercent'],
        key: 'onTimePercent',
        render: val => {
          const value = val || 0;
          let color = 'green';
          if (value < 70) color = 'blue';
          else if (value < 90) color = 'red';
          
          return (
            <Tooltip title={`${Math.round(value)}% on time for entire trip`}>
              <Progress 
                percent={Math.round(value)} 
                size="small" 
                strokeColor={color}
                format={percent => `${percent}%`}
              />
            </Tooltip>
          );
        },
        sorter: (a, b) => (a.overallStats?.onTimePercent || 0) - (b.overallStats?.onTimePercent || 0),
      },
      {
        title: 'First Stop OTR',
        dataIndex: ['firstStopStats', 'onTimePercent'],
        key: 'firstStopOTR',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.firstStopStats?.onTimePercent || 0) - (b.firstStopStats?.onTimePercent || 0),
      },
      {
        title: 'Mid Stop OTR',
        dataIndex: ['midStopStats', 'onTimePercent'],
        key: 'midStopOTR',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.midStopStats?.onTimePercent || 0) - (b.midStopStats?.onTimePercent || 0),
      },
      {
        title: 'Last Stop OTR',
        dataIndex: ['lastStopStats', 'onTimePercent'],
        key: 'lastStopOTR',
        render: val => `${Math.round(val || 0)}%`,
        sorter: (a, b) => (a.lastStopStats?.onTimePercent || 0) - (b.lastStopStats?.onTimePercent || 0),
      },
      {
        title: 'Total Stops',
        dataIndex: 'stopCount',
        key: 'stopCount',
        sorter: (a, b) => (a.stopCount || 0) - (b.stopCount || 0),
      }
    ];

    return (
      <Card 
        title={
          <Space>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => setSelectedStopDetail(null)}
            >
              Back
            </Button>
            {`Trip Performance for Stop ${selectedStopDetail.tsn} ${selectedStopDetail.stopName ? `(${selectedStopDetail.stopName})` : ''}`}
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={stopTripData}
          rowKey="tripId"
          pagination={{ pageSize: 20 }}
        />
      </Card>
    );
  };

  // Filter stops based on search text
  const filteredStopStats = stopSearchText 
    ? stopStatsArray.filter(stop => 
        String(stop.tsn).toLowerCase().includes(stopSearchText.toLowerCase()) || 
        (stop.stopName && stop.stopName.toLowerCase().includes(stopSearchText.toLowerCase()))
      )
    : stopStatsArray;

  // Define columns for the stops table
  const stopColumns = [
    {
      title: 'Stop ID',
      dataIndex: 'tsn',
      key: 'tsn',
      sorter: (a, b) => String(a.tsn).localeCompare(String(b.tsn)),
      render: tsn => (
        <Button
            type="link"
            style={{
              padding: 0,
              fontSize: '14px',
              fontWeight: 'bold',
              textDecoration: 'underline'
            }}
            onClick={() => handleStopClick(tsn)}>{tsn}</Button>
      ),
      width: 80,
    },
    {
      title: 'Stop Name',
      dataIndex: 'stopName',
      key: 'stopName',
      sorter: (a, b) => String(a.stopName).localeCompare(String(b.stopName)),
      render: (stopName) => (
        <span style={{ fontSize: '14px' }}>{stopName || 'Unknown Stop'}</span>
      ),
      width: 100,
    },
    {
      title: 'Trip Count',
      dataIndex: 'tripCount',
      key: 'tripCount',
      sorter: (a, b) => (a.tripCount || 0) - (b.tripCount || 0),
      width: 80,
    },
    {
      title: 'Overall OTR %',
      dataIndex: 'onTimePercent',
      key: 'overallOTR',
      render: (val, record) => {
        const value = val || 0;
        let color = 'green';
        if (value < 70) color = 'blue';
        else if (value < 90) color = 'red';
        
        return (
          <Tooltip title={`${Math.round(value)}% on time\n${Math.round(record.earlyPercent || 0)}% early\n${Math.round(record.latePercent || 0)}% late`}>
            <Progress 
              percent={Math.round(value)} 
              size="small" 
              strokeColor={color}
              format={percent => `${percent}%`}
            />
          </Tooltip>
        );
      },
      sorter: (a, b) => a.onTimePercent - b.onTimePercent,
      width: 100,
    },
    {
      title: 'AI Insights',
      key: 'insights',
      dataIndex: 'insights',
      render: (_, record) => {
        // Generate AI insights using the ruleset
        const aiInsight = analyzeStopWithRuleset(record, stopData);
        
        if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
          return <Tag color="default">No insights</Tag>;
        }
        
        // Create a condensed insight indicator based on the type of insight
        let insightType = "Issue";
        let tagColor = "#f50";
        
        // Categorize insights by common types for short display
        if (aiInsight.insight.includes("chronic lateness")) {
          insightType = "Chronic delays";
          tagColor = "#1890ff"; // Blue
        } else if (aiInsight.insight.includes("dwell time")) {
          insightType = "Dwell time";
          tagColor = "#fa8c16"; // Orange
        } else if (aiInsight.insight.includes("inconsistent")) {
          insightType = "Route inconsistency";
          tagColor = "#722ed1"; // Purple
        }
        
        // Add improvement percentage if available
        const improvementText = aiInsight.improvement ? ` +${aiInsight.improvement}%` : '';
        
        // Store insight type and improvement in record for sorting/filtering
        record.insightType = insightType;
        record.insightImprovement = aiInsight.improvement || 0;
        
        return (
          <Popover 
            content={
              <OTRInsightCard 
                title={`Stop ${record.tsn} Insights`}
                insight={aiInsight.insight}
                recommendation={aiInsight.recommendation}
                improvement={aiInsight.improvement}
                category="stop"
              />
            } 
            title={null}
            trigger="click"
          >
            <Tag 
              color={tagColor}
              icon={<BulbOutlined />}
              style={{ cursor: 'pointer' }}
            >
              {insightType}{improvementText}
            </Tag>
          </Popover>
        );
      },
      sorter: (a, b) => {
        const insightA = analyzeStopWithRuleset(a, stopData);
        const insightB = analyzeStopWithRuleset(b, stopData);
        
        const improvementA = insightA?.improvement || 0;
        const improvementB = insightB?.improvement || 0;
        
        return improvementB - improvementA; // Sort by highest improvement first
      },
      filters: [
        { text: 'Chronic delays', value: 'Chronic delays' },
        { text: 'Dwell time', value: 'Dwell time' },
        { text: 'Route inconsistency', value: 'Route inconsistency' },
        { text: 'No issues', value: 'No insights' }
      ],
      onFilter: (value, record) => {
        const aiInsight = analyzeStopWithRuleset(record, stopData);
        
        if (!aiInsight || (!aiInsight.insight && !aiInsight.recommendation)) {
          return value === 'No insights';
        }
        
        let insightType = "Issue";
        
        // Categorize for filtering
        if (aiInsight.insight.includes("chronic lateness")) {
          insightType = "Chronic delays";
        } else if (aiInsight.insight.includes("dwell time")) {
          insightType = "Dwell time";
        } else if (aiInsight.insight.includes("inconsistent")) {
          insightType = "Route inconsistency";
        }
        
        return insightType === value;
      },
      width: 180,
    },
  ];

  return (
    <div>
      {selectedStopDetail ? (
        renderStopTripView()
      ) : (
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card 
              title={
                <Space>
                  <span>Stop Level Performance Statistics</span>
                  <Tooltip title="This table shows OTR performance metrics for each stop in the network. Use the filters and search to find specific stops.">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </Space>
              }
            >
              {stopsWithIssues.length > 0 && (
                <Alert
                  message="Some stops show patterns suggesting potential positioning or timing data issues"
                  description="Use the filters to identify stops that may need attention to improve OTR performance."
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}
              
              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                <Input.Search
                  placeholder="Search by Stop ID (TSN) or Stop Name"
                  onChange={e => setStopSearchText(e.target.value)}
                  onSearch={value => setStopSearchText(value)}
                  value={stopSearchText}
                  allowClear
                  enterButton
                  style={{ width: 400 }}
                />
                {stopSearchText && (
                  <>
                    <Text style={{ margin: '0 16px' }}>
                      Found {filteredStopStats.length} {filteredStopStats.length === 1 ? 'stop' : 'stops'} matching "{stopSearchText}"
                    </Text>
                    <Button 
                      type="link" 
                      onClick={() => setStopSearchText('')}
                      style={{ padding: '0 8px' }}
                    >
                      Reset
                    </Button>
                  </>
                )}
              </div>
              
              <Table
                dataSource={filteredStopStats}
                columns={stopColumns}
                rowKey="tsn"
                pagination={{ pageSize: 20 }}
              />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default OTRStopPerformanceTab; 